import { Post } from '../types';

/**
 * V4-NEWSLETTER-TEMPLATES-FR-ONLY-DUPLICATED 2026-07-30 — source unique du corps
 * d'email newsletter.
 *
 * Avant : 4 gabarits HTML quasi identiques, **tous en français en dur**, alors que
 * l'app sert 3 locales (un utilisateur ES envoyait une newsletter française) :
 *   - `Constants.DEFAULT_NEWSLETTER_TEMPLATE` (graine de l'éditeur)
 *   - `newsletterUtils.generateNewsletterHtml` (onglet Newsletter)
 *   - `NewsletterPostModal.generateHTML` (variante « veille quotidienne »)
 *   - `newsletterService.generateNewsletterHtml` (orphelin, figé à « © 2024 »)
 *
 * Désormais un seul `buildNewsletterHtml`, et la copie vient de `t.newsletterTemplate`
 * (fr/en/es). Toute évolution du gabarit se fait ICI et nulle part ailleurs.
 *
 * ⚠️ L'ancienne signature `generateNewsletterHtml(posts, template)` déclarait un
 * paramètre `template` qu'elle n'utilisait JAMAIS : le gabarit édité par
 * l'utilisateur était silencieusement remplacé par le HTML en dur, et l'unique
 * appelant lui passait en réalité un titre. Le paramètre a disparu avec le bug.
 */

/** Copie traduisible du gabarit — correspond au namespace i18n `newsletterTemplate`. */
export interface NewsletterTemplateCopy {
  documentTitle: string;
  heading: string;
  subtitle: string;
  introHeading: string;
  introBody: string;
  ctaHeading: string;
  ctaLabel: string;
  postImageAlt: string;
  rights: string;
  subscriptionNotice: string;
}

export interface NewsletterTemplateOptions {
  /** Attribut `lang` du document (`fr` | `en` | `es`). */
  lang?: string;
  /**
   * Introduction rédigée par l'utilisateur (texte brut). Quand elle est fournie, elle
   * remplace le bloc d'introduction générique de `copy.introHeading`/`introBody`.
   */
  introduction?: string;
  /** Bloc d'appel à l'action final. Désactivable (variante du modal de post). */
  showCta?: boolean;
}

/**
 * Le contenu des posts et l'introduction viennent de l'utilisateur et partent dans un
 * email : on échappe avant d'interpoler, sinon un simple `<` dans un post casse le
 * rendu (et permet d'injecter du balisage arbitraire chez le destinataire).
 */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const toParagraph = (value: string): string => escapeHtml(value).replace(/\n/g, '<br>');

export const buildNewsletterHtml = (
  posts: Post[],
  copy: NewsletterTemplateCopy,
  options: NewsletterTemplateOptions = {},
): string => {
  const { lang = 'fr', introduction, showCta = true } = options;

  const postsHtml = posts
    .map(
      (post) => `      <div class="feature-card">
${post.imageUrl ? `        <img src="${escapeHtml(post.imageUrl)}" alt="${escapeHtml(copy.postImageAlt)}" class="feature-image" />\n` : ''}        <div class="feature-content">
          <p>${toParagraph(post.content)}</p>
        </div>
      </div>`,
    )
    .join('\n');

  const introHtml = introduction?.trim()
    ? `      <div class="section" id="introduction">
        <p>${toParagraph(introduction)}</p>
      </div>`
    : `      <div class="section">
        <h2>${escapeHtml(copy.introHeading)}</h2>
        <p>${escapeHtml(copy.introBody)}</p>
      </div>`;

  const ctaHtml = showCta
    ? `      <div class="section" style="text-align: center;">
        <h2>${escapeHtml(copy.ctaHeading)}</h2>
        <a href="#" class="cta-button">${escapeHtml(copy.ctaLabel)}</a>
      </div>`
    : '';

  return `<!DOCTYPE html>
<html lang="${escapeHtml(lang)}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(copy.documentTitle)}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      line-height: 1.6;
      color: #2d3748;
      margin: 0;
      padding: 0;
      background-color: #f7fafc;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      background-color: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 40px 20px;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      font-size: 28px;
      font-weight: 700;
    }
    .header p {
      margin: 10px 0 0;
      opacity: 0.9;
      font-size: 16px;
    }
    .content {
      padding: 40px;
    }
    .section {
      margin-bottom: 32px;
    }
    .section h2 {
      color: #2d3748;
      font-size: 20px;
      margin-bottom: 16px;
      border-bottom: 2px solid #edf2f7;
      padding-bottom: 8px;
    }
    .feature-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      margin-bottom: 24px;
      overflow: hidden;
    }
    .feature-image {
      width: 100%;
      height: 200px;
      object-fit: cover;
    }
    .feature-content {
      padding: 20px;
    }
    .feature-content p {
      margin: 0;
      color: #718096;
      font-size: 14px;
    }
    .cta-button {
      display: inline-block;
      background-color: #667eea;
      color: white;
      text-decoration: none;
      padding: 12px 24px;
      border-radius: 6px;
      font-weight: 600;
      margin: 20px 0;
    }
    .footer {
      background: #f7fafc;
      padding: 32px 40px;
      text-align: center;
      color: #718096;
      font-size: 14px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>${escapeHtml(copy.heading)}</h1>
      <p>${escapeHtml(copy.subtitle)}</p>
    </div>

    <div class="content">
${introHtml}
${postsHtml}
${ctaHtml}
    </div>

    <div class="footer">
      <p>© ${new Date().getFullYear()} - ${escapeHtml(copy.rights)}</p>
      <p>${escapeHtml(copy.subscriptionNotice)}</p>
    </div>
  </div>
</body>
</html>`;
};
